// Phone configuration for the application
// This file can be easily modified to support different countries or requirements

export interface PhoneConfig {
  defaultCountryCode: string;
  countryName: string;
  minLength: number;
  maxLength: number;
  allowedCharacters: RegExp;
  internationalPrefix: string;
  patterns: {
    mobile: RegExp;
    landline: RegExp;
    tollFree: RegExp;
    emergency: RegExp;
  };
  placeholders: {
    local: string;
    international: string;
    combined: string;
  };
  errorMessages: {
    required: string;
    invalidFormat: string;
    tooShort: string;
    tooLong: string;
    invalidCharacters: string;
    duplicate: string;
    notFound: string;
    landlineNotAllowed: string;
    invalidInternationalFormat: string;
    internationalLengthError: string;
  };
}

// Colombia configuration
export const COLOMBIA_PHONE_CONFIG: PhoneConfig = {
  defaultCountryCode: '+57',
  countryName: 'Colombia',
  minLength: 10,
  maxLength: 15,
  allowedCharacters: /^[\d\s\-\(\)\+]+$/,
  internationalPrefix: '+',
  patterns: {
    mobile: /^(\+57)?(3\d{9})$/,
    landline: /^(\+57)?([1-8]\d{7})$/,
    tollFree: /^(\+57)?(1[8-9]\d{7})$/,
    emergency: /^(\+57)?(1[1-3][0-9])$/
  },
  placeholders: {
    local: 'Ej: 3028891213',
    international: 'Ej: +13135957876',
    combined: 'Ej: 3028891213 o +13135957876'
  },
  errorMessages: {
    required: 'El número de teléfono es requerido',
    invalidFormat: 'Formato de número de teléfono inválido',
    tooShort: 'El número de teléfono debe tener al menos 10 dígitos',
    tooLong: 'El número de teléfono no puede exceder 15 dígitos',
    invalidCharacters: 'El número de teléfono contiene caracteres inválidos',
    duplicate: 'Este número de teléfono ya está registrado',
    notFound: 'Número de teléfono no encontrado',
    landlineNotAllowed: 'No se permite registrar teléfonos fijos porque no pueden recibir SMS. Por favor, ingresa un número de celular.',
    invalidInternationalFormat: 'Formato de número internacional inválido',
    internationalLengthError: 'El número internacional debe tener entre 7 y 15 dígitos'
  }
};

// US configuration (for reference)
export const US_PHONE_CONFIG: PhoneConfig = {
  defaultCountryCode: '+1',
  countryName: 'United States',
  minLength: 10,
  maxLength: 15,
  allowedCharacters: /^[\d\s\-\(\)\+]+$/,
  internationalPrefix: '+',
  patterns: {
    mobile: /^(\+1)?(\d{10})$/,
    landline: /^(\+1)?(\d{10})$/,
    tollFree: /^(\+1)?(1[8-9]\d{9})$/,
    emergency: /^(\+1)?(911)$/
  },
  placeholders: {
    local: 'Example: 5551234567',
    international: 'Example: +13135957876',
    combined: 'Example: 5551234567 or +13135957876'
  },
  errorMessages: {
    required: 'Phone number is required',
    invalidFormat: 'Invalid phone number format',
    tooShort: 'Phone number must be at least 10 digits',
    tooLong: 'Phone number cannot exceed 15 digits',
    invalidCharacters: 'Phone number contains invalid characters',
    duplicate: 'This phone number is already registered',
    notFound: 'Phone number not found',
    landlineNotAllowed: 'Landline numbers are not allowed for registration because they cannot receive SMS. Please enter a mobile number.',
    invalidInternationalFormat: 'Invalid international phone number format',
    internationalLengthError: 'International phone number must be between 7 and 15 digits'
  }
};

// Mexico configuration (for reference)
export const MEXICO_PHONE_CONFIG: PhoneConfig = {
  defaultCountryCode: '+52',
  countryName: 'Mexico',
  minLength: 10,
  maxLength: 15,
  allowedCharacters: /^[\d\s\-\(\)\+]+$/,
  internationalPrefix: '+',
  patterns: {
    mobile: /^(\+52)?(1\d{9})$/,
    landline: /^(\+52)?([2-9]\d{8})$/,
    tollFree: /^(\+52)?(800\d{7})$/,
    emergency: /^(\+52)?(911)$/
  },
  placeholders: {
    local: 'Ej: 5512345678',
    international: 'Ej: +13135957876',
    combined: 'Ej: 5512345678 o +13135957876'
  },
  errorMessages: {
    required: 'El número de teléfono es requerido',
    invalidFormat: 'Formato de número de teléfono inválido',
    tooShort: 'El número de teléfono debe tener al menos 10 dígitos',
    tooLong: 'El número de teléfono no puede exceder 15 dígitos',
    invalidCharacters: 'El número de teléfono contiene caracteres inválidos',
    duplicate: 'Este número de teléfono ya está registrado',
    notFound: 'Número de teléfono no encontrado',
    landlineNotAllowed: 'No se permite registrar teléfonos fijos porque no pueden recibir SMS. Por favor, ingresa un número de celular.',
    invalidInternationalFormat: 'Formato de número internacional inválido',
    internationalLengthError: 'El número internacional debe tener entre 7 y 15 dígitos'
  }
};

// Default configuration (currently Colombia)
export const DEFAULT_PHONE_CONFIG = COLOMBIA_PHONE_CONFIG;

// Function to get configuration by country code
export function getPhoneConfig(countryCode: string): PhoneConfig {
  switch (countryCode) {
    case '+57':
      return COLOMBIA_PHONE_CONFIG;
    case '+1':
      return US_PHONE_CONFIG;
    case '+52':
      return MEXICO_PHONE_CONFIG;
    default:
      return DEFAULT_PHONE_CONFIG;
  }
}

// Function to get configuration by country name
export function getPhoneConfigByCountry(countryName: string): PhoneConfig {
  switch (countryName.toLowerCase()) {
    case 'colombia':
      return COLOMBIA_PHONE_CONFIG;
    case 'united states':
    case 'usa':
    case 'us':
      return US_PHONE_CONFIG;
    case 'mexico':
      return MEXICO_PHONE_CONFIG;
    default:
      return DEFAULT_PHONE_CONFIG;
  }
}

// Environment-based configuration
export function getCurrentPhoneConfig(): PhoneConfig {
  // You can modify this to use environment variables or other logic
  const countryFromEnv = import.meta.env.VITE_DEFAULT_COUNTRY;
  
  if (countryFromEnv) {
    return getPhoneConfigByCountry(countryFromEnv);
  }
  
  return DEFAULT_PHONE_CONFIG;
} 